/**
 * Programmatic SEO landing page — `/puppies/:breedSlug/:stateSlug`
 *
 * Why: long-tail Google queries like "french bulldog puppies texas"
 * are extremely high-intent buyer traffic. The main /marketplace page
 * is a general search; these are content-rich, breed-+-state-specific
 * pages that rank for those queries.
 *
 * Each page surfaces:
 *  - Hero: "{Breed} puppies for sale in {State}" headline + N listings
 *  - Trust strip: escrow · verified breeders · venue posture
 *  - Listing grid (lightweight DogCard-style, links to /d/{slug})
 *  - Breed snippet (from catalog) + state breeder count
 *  - JSON-LD ItemList for rich-result eligibility
 *  - Canonical link tag
 */
import React, { useEffect, useState } from "react";
import { webOrigin } from "@/lib/nativeBridge";
import { useParams, Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import api from "@/lib/api";
import AppShell from "@/components/AppShell";
import { Skeleton } from "@/components/ui/skeleton";
import { ShieldCheckIcon as ShieldCheck, CheckCircleIcon as CheckCircle, MapPinIcon as MapPin, ArrowRightIcon as ArrowRight } from "@phosphor-icons/react";

export default function PuppiesByBreedState() {
  const { breedSlug, stateSlug } = useParams();
  const [data, setData] = useState(null);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    setData(null);
    setNotFound(false);
    api
      .get(`/public/puppies-by-breed-state/${breedSlug}/${stateSlug}`, { silent: true })
      .then((r) => setData(r.data || {}))
      .catch((e) => {
        if (e?.response?.status === 404) setNotFound(true);
        else setData({ listings: [], breed: "", state: "", breeder_count: 0 });
      });
  }, [breedSlug, stateSlug]);

  if (notFound) {
    return (
      <AppShell>
        <div className="max-w-2xl mx-auto px-4 py-20 text-center">
          <h1 className="font-spectral text-4xl mb-3">Page not found</h1>
          <p className="text-[#6B6B6B] mb-6">
            We don&rsquo;t have a directory for that breed in that state yet.
          </p>
          <Link to="/marketplace" className="text-crimson font-bold underline">
            Browse the full marketplace →
          </Link>
        </div>
      </AppShell>
    );
  }

  if (!data) {
    return (
      <AppShell>
        <div className="max-w-6xl mx-auto px-4 py-10 space-y-4">
          <Skeleton className="h-16 w-3/4" />
          <Skeleton className="h-6 w-1/2" />
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4 mt-6">
            {[1, 2, 3, 4, 5, 6].map((i) => <Skeleton key={i} className="h-80" />)}
          </div>
        </div>
      </AppShell>
    );
  }

  const { breed, state, state_code, listings = [], breeder_count = 0, breed_description, exotic } = data;
  const count = listings.length;
  const title = count > 0
    ? `${breed} puppies for sale in ${state} (${count} listed) | Armstrong K9 Registry`
    : `${breed} breeders in ${state} | Armstrong K9 Registry`;
  const description = count > 0
    ? `${count} verified ${breed} ${count === 1 ? "puppy" : "puppies"} for sale in ${state}, from ${breeder_count} ${breeder_count === 1 ? "breeder" : "breeders"}. Every sale runs through Armstrong K9 escrow — funds release only when the dog is safely transferred.`
    : `Browse verified ${breed} breeders in ${state}. Armstrong K9 Registry runs every sale through escrow — funds release only when the dog is safely transferred.`;
  const canonical = typeof window !== "undefined"
    ? `${webOrigin()}/puppies/${breedSlug}/${stateSlug}`
    : `/puppies/${breedSlug}/${stateSlug}`;

  // ItemList schema so the grid is rich-result eligible.
  const jsonLd = {
    "@context": "https://schema.org",
    "@type": "ItemList",
    "itemListElement": listings.slice(0, 20).map((d, i) => ({
      "@type": "ListItem",
      "position": i + 1,
      "name": d.name || `${breed} puppy`,
      "url": `${canonical.split("/puppies")[0]}/d/${d.slug || d.id}`,
    })),
    "numberOfItems": count,
  };

  return (
    <AppShell>
      <Helmet>
        <title>{title}</title>
        <meta name="description" content={description} />
        <link rel="canonical" href={canonical} />
        <meta property="og:title" content={title} />
        <meta property="og:description" content={description} />
        <meta property="og:type" content="website" />
        <meta property="og:url" content={canonical} />
        <meta name="twitter:card" content="summary_large_image" />
        <script type="application/ld+json">{JSON.stringify(jsonLd)}</script>
      </Helmet>

      <div className="max-w-6xl mx-auto px-4 py-10" data-testid="puppies-breed-state-page">
        {/* Hero */}
        <header className="mb-8">
          <p className="text-[10px] uppercase tracking-[0.3em] text-gold font-bold mb-2">
            <MapPin size={11} weight="duotone" className="inline mr-1" />
            {state} · {state_code}
          </p>
          <h1 className="font-spectral text-4xl sm:text-5xl lg:text-6xl tracking-tight leading-tight">
            {breed} puppies in {state}
          </h1>
          <p className="text-base sm:text-lg text-[#3F3F3F] mt-3 max-w-3xl leading-relaxed">
            {count > 0
              ? `${count} verified ${count === 1 ? "puppy" : "puppies"} currently listed from ${breeder_count} ${breeder_count === 1 ? "breeder" : "breeders"}.`
              : `No active ${breed} listings in ${state} right now.`}{" "}
            Every sale runs through Armstrong K9 escrow — funds release only when the dog is safely transferred.
          </p>
          {exotic && (
            <span className="inline-flex items-center gap-1.5 mt-3 text-[11px] uppercase tracking-wider font-bold text-[#8A6A00] bg-[#FBF1D2] border border-[#E8D9A6] px-2.5 py-1 rounded-full">
              Exotic breed · Premium pricing
            </span>
          )}
        </header>

        {/* Trust strip */}
        <div className="grid sm:grid-cols-3 gap-3 mb-10">
          <TrustTile
            icon={<ShieldCheck size={20} weight="duotone" />}
            title="Verified breeders"
            blurb="Every listed kennel is identity-verified by Armstrong."
          />
          <TrustTile
            icon={<CheckCircle size={20} weight="duotone" />}
            title="Escrow-protected"
            blurb="Funds release only after the dog is safely with you."
          />
          <TrustTile
            icon={<MapPin size={20} weight="duotone" />}
            title="Transport built-in"
            blurb="Verified USDA-licensed haulers cover all 50 states."
          />
        </div>

        {/* Breed snippet */}
        {breed_description && (
          <section className="mb-10 bg-cream/40 border border-[#E2DAC2] rounded-2xl p-5">
            <h2 className="font-spectral text-2xl mb-2">About the {breed}</h2>
            <p className="text-[14px] text-[#3F3F3F] leading-relaxed">{breed_description}</p>
          </section>
        )}

        {/* Listings grid */}
        {count > 0 ? (
          <section className="mb-12">
            <h2 className="font-spectral text-2xl mb-4">
              Available {breed} puppies in {state}
            </h2>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
              {listings.map((d) => (
                <ListingCard key={d.id} dog={d} />
              ))}
            </div>
          </section>
        ) : (
          <section className="mb-12 text-center py-10">
            <p className="text-[#6B6B6B] mb-4">
              No active listings right now. Set up an alert and we&rsquo;ll email you when one matches.
            </p>
            <Link
              to="/marketplace"
              className="inline-flex items-center gap-2 bg-ink hover:bg-crimson text-white rounded-full px-5 py-3 font-bold transition-colors"
              data-testid="empty-state-marketplace-cta"
            >
              Browse the full marketplace <ArrowRight size={14} weight="bold" />
            </Link>
          </section>
        )}

        {/* Cross-persona handoffs */}
        <section className="mb-10 border-t border-[#E5E5E5] pt-8">
          <h2 className="font-spectral text-2xl mb-4">After you bring your puppy home</h2>
          <div className="grid sm:grid-cols-3 gap-3 text-[13px]">
            <Link to="/find-pros" className="block bg-white hover:bg-cream/30 border border-[#E5E5E5] rounded-xl p-4 transition-colors">
              <p className="font-bold text-ink mb-1">Find a trainer or groomer →</p>
              <p className="text-[#6B6B6B] leading-snug">Verified service providers near you.</p>
            </Link>
            <Link to="/transport" className="block bg-white hover:bg-cream/30 border border-[#E5E5E5] rounded-xl p-4 transition-colors">
              <p className="font-bold text-ink mb-1">Book transport →</p>
              <p className="text-[#6B6B6B] leading-snug">USDA-licensed haulers, escrow protected.</p>
            </Link>
            <Link to="/storefronts" className="block bg-white hover:bg-cream/30 border border-[#E5E5E5] rounded-xl p-4 transition-colors">
              <p className="font-bold text-ink mb-1">Shop the Dog Shop →</p>
              <p className="text-[#6B6B6B] leading-snug">Food, treats, and merch from kennel storefronts.</p>
            </Link>
          </div>
        </section>
      </div>
    </AppShell>
  );
}

function TrustTile({ icon, title, blurb }) {
  return (
    <div className="bg-white border border-[#E5E5E5] rounded-xl p-4">
      <div className="flex items-center gap-2 mb-1 text-gold">
        {icon}
        <p className="text-[11px] uppercase tracking-wider font-bold text-ink">{title}</p>
      </div>
      <p className="text-[12.5px] text-[#6B6B6B] leading-snug">{blurb}</p>
    </div>
  );
}

function ListingCard({ dog }) {
  const url = `/d/${dog.slug || dog.id}`;
  const photo = (dog.photos && dog.photos[0]) || dog.photo_url || null;
  const price = dog.asking_price ? `$${Math.round(dog.asking_price).toLocaleString()}` : null;
  return (
    <Link
      to={url}
      data-testid={`seo-listing-${dog.id}`}
      className="block bg-white border border-[#E5E5E5] rounded-2xl overflow-hidden hover:shadow-md transition-shadow"
    >
      <div className="aspect-square bg-cream/30 relative overflow-hidden">
        {photo ? (
          <img src={photo} alt={dog.name || dog.breed} className="w-full h-full object-cover" loading="lazy" />
        ) : (
          <div className="flex items-center justify-center h-full text-[#9A9A9A] text-[11px] uppercase tracking-wider">
            {dog.breed}
          </div>
        )}
        {dog.seller_verified && (
          <span className="absolute top-2 left-2 inline-flex items-center gap-1 bg-emerald-50/95 border border-emerald-200 text-emerald-900 text-[10px] uppercase tracking-wider font-bold rounded-full px-2 py-0.5">
            <ShieldCheck size={10} weight="fill" /> Verified
          </span>
        )}
      </div>
      <div className="p-3">
        <p className="font-spectral text-lg leading-tight truncate">{dog.name || dog.breed}</p>
        <p className="text-[12px] text-[#6B6B6B] mt-0.5 truncate">
          {dog.seller_kennel_name || "—"}{dog.seller_location ? ` · ${dog.seller_location}` : ""}
        </p>
        {price && (
          <p className="font-tabular text-[15px] font-bold text-crimson mt-2">{price}</p>
        )}
      </div>
    </Link>
  );
}
